/**
 * Read / write the active sub-view via the URL **pathname**. Each sub-view
 * lives at its own route (`/just-pulled`, `/my-packs`); the default Pack
 * page is `/`. Query params — notably `game_id` and `session_token` —
 * are preserved across transitions so shareable links keep working and
 * the rest of the app continues to read them.
 *
 * Previously the view sat in a `?view=…` query param. Migrating to
 * pathname makes shareable links cleaner (`/my-packs?game_id=8` instead
 * of `/?game_id=8&view=my-packs`) and lets each view own a separate
 * `data-page` background scope without router conflicts.
 */
export type IframeView = 'my-packs' | 'just-pulled';

const VIEW_TO_PATH: Record<IframeView, string> = {
  'just-pulled': '/just-pulled',
  'my-packs': '/my-packs',
};

const ROOT_PATH = '/';

/**
 * Match a pathname to the top-level iframe view. Each view owns a path
 * prefix — `/my-packs/deliveries/new` and `/my-packs/deliveries/:id`
 * are still `my-packs` so the PackPage shell keeps the sub-view
 * mounted while MyPacksPage drives its own internal routing.
 */
function pathToView(pathname: string): IframeView | null {
  if (pathname === '/just-pulled' || pathname.startsWith('/just-pulled/')) {
    return 'just-pulled';
  }
  if (pathname === '/my-packs' || pathname.startsWith('/my-packs/')) {
    return 'my-packs';
  }
  return null;
}

export function setViewInURL(view: IframeView | null): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  // When the URL is already inside the requested view's subtree
  // (e.g. /my-packs/deliveries/new while setView('my-packs') is called)
  // leave the pathname alone — sub-routes own their state and the
  // shell view is already correct.
  const currentView = pathToView(url.pathname);
  const targetPath = view ? VIEW_TO_PATH[view] : ROOT_PATH;
  // Also strip the legacy `?view=…` query so the URL doesn't carry both
  // representations after the first navigation.
  url.searchParams.delete('view');
  const wasSamePath =
    url.pathname === targetPath || (view !== null && currentView === view);
  if (!wasSamePath) url.pathname = targetPath;
  // `history.replaceState` so back/forward navigation between Pack page
  // and a sub-view doesn't bury the operator under a long history stack.
  // The Pack template's React state is the source of truth — URL just
  // mirrors it.
  window.history.replaceState(null, '', url);
  // Tell the parent Constructor (if we're embedded as the preview iframe)
  // that the pathname changed — SPA-style navigation produces no `load`
  // event, so without this message the parent's scene filter would lag
  // a navigation behind.
  if (!wasSamePath && window.parent !== window) {
    window.parent.postMessage(
      { type: 'design-constructor-route', pathname: url.pathname },
      '*',
    );
  }
}

export function getViewFromURL(): IframeView | null {
  if (typeof window === 'undefined') return null;
  const fromPath = pathToView(window.location.pathname);
  if (fromPath) return fromPath;
  // Legacy fallback — support old `?view=…` bookmarks. The next call to
  // `setViewInURL` rewrites the URL to the new pathname form.
  const legacy = new URL(window.location.href).searchParams.get('view');
  if (legacy === 'my-packs' || legacy === 'just-pulled') return legacy;
  return null;
}
